/**
 * GlobalSearchDialog — universele ⌘K / Ctrl+K zoek modal.
 *
 * Features:
 *   - Debounced backend search naar /admin/global-search
 *   - Resultaten gegroepeerd per categorie
 *   - Toont Recent bezocht als het zoekveld leeg is
 *   - Keyboard navigatie: ↑ ↓ Enter Esc
 *   - Backend-drive: uitbreidbaar zonder frontend-wijzigingen
 *
 * Opent via:
 *   - ⌘K / Ctrl+K
 *   - Trigger-button in de topbar
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ArrowUp, ArrowDown, CornerDownLeft, Loader2, Clock, Sparkles } from "lucide-react";
import { adminApi } from "../../../context/AuthContext";
import { useUserPreferences } from "../../../context/UserPreferencesContext";
import { CATEGORY_ICONS, NAV_ITEMS_FLAT } from "./nav-config";

const KEY_TIP = (typeof window !== "undefined" && window.navigator?.platform?.toLowerCase().includes("mac"))
  ? "⌘"
  : "Ctrl";

export default function GlobalSearchDialog({ open, onOpenChange, onNavigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ categories: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const { prefs, recordVisit } = useUserPreferences();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setActiveIdx(0);
    } else {
      setQ("");
      setResults({ categories: [], total: 0 });
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const term = (q || "").trim();
    if (term.length < 2) {
      setResults({ categories: [], total: 0 });
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await adminApi.get("/admin/global-search", {
          params: { q: term, limit: 5 },
        });
        setResults(r.data || { categories: [], total: 0 });
        setActiveIdx(0);
      } catch {
        setResults({ categories: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  // Flatten voor keyboard-navigatie
  const flat = useMemo(() => {
    const list = [];
    if ((q || "").trim().length < 2) {
      // Recent bezocht als zoekveld leeg
      (prefs.recent_visits || []).forEach((v) => {
        list.push({
          _kind: "recent",
          category_label: "Recent bezocht",
          icon: "Clock",
          title: v.entity_label || v.label,
          meta: v.entity_id ? `${v.label} · ${v.entity_id}` : v.label,
          nav_key: v.key,
          entity_id: v.entity_id,
        });
      });
    }
    results.categories.forEach((c) => {
      c.items.forEach((it) => list.push({
        ...it, _kind: "result",
        category_label: c.label,
      }));
    });
    return list;
  }, [results, q, prefs.recent_visits]);

  const handleSelect = useCallback((item) => {
    if (!item) return;
    // Record visit
    if (item.nav_key) {
      const navItem = NAV_ITEMS_FLAT[item.nav_key];
      recordVisit({
        key: item.nav_key,
        label: navItem?.item?.label || item.nav_key,
        entity_id: item.entity_id || null,
        entity_label: item.title,
      });
      onNavigate?.(item.nav_key, item);
    }
    onOpenChange(false);
  }, [onNavigate, onOpenChange, recordVisit]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(flat[activeIdx]);
    } else if (e.key === "Escape") {
      onOpenChange(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm p-4 sm:p-10 flex items-start justify-center animate-in fade-in duration-150"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
      data-testid="global-search-overlay"
    >
      <div
        className="w-full max-w-2xl bg-white rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] overflow-hidden flex flex-col border border-slate-200 animate-in zoom-in-95 slide-in-from-top-4 duration-200"
        data-testid="global-search-dialog"
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Zoek klanten, facturen, offertes, trackingnummers, containers, vluchten…"
            className="flex-1 bg-transparent text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
            aria-label="Sluiten"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto max-h-[60vh]" data-testid="global-search-results">
          {(q || "").trim().length < 2 ? (
            <RecentSection
              visits={prefs.recent_visits || []}
              activeIdx={activeIdx}
              onSelect={handleSelect}
            />
          ) : results.total === 0 && !loading ? (
            <EmptyState q={q} />
          ) : (
            <ResultsList
              categories={results.categories}
              activeIdx={activeIdx}
              onSelect={handleSelect}
            />
          )}
        </div>

        {/* Footer — keyboard hints */}
        <div className="flex items-center gap-4 px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500">
          <KbdHint icon={ArrowUp} extraIcon={ArrowDown} label="navigeer" />
          <KbdHint icon={CornerDownLeft} label="openen" />
          <span className="ml-auto flex items-center gap-1.5">
            <kbd className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-500">esc</kbd>
            <span>sluiten</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function KbdHint({ icon: Icon, extraIcon: ExtraIcon, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <kbd className="font-mono bg-white border border-slate-200 rounded p-1">
        <Icon className="w-3 h-3" />
      </kbd>
      {ExtraIcon && (
        <kbd className="font-mono bg-white border border-slate-200 rounded p-1">
          <ExtraIcon className="w-3 h-3" />
        </kbd>
      )}
      <span>{label}</span>
    </span>
  );
}

function EmptyState({ q }) {
  return (
    <div className="p-8 text-center text-sm text-slate-500" data-testid="global-search-empty">
      <Search className="w-8 h-8 mx-auto mb-3 text-slate-300" />
      Geen resultaten voor <strong className="text-slate-700">&quot;{q}&quot;</strong>.
      <div className="mt-2 text-xs text-slate-400">Probeer een deel van de klantnaam, factuurnummer of trackingcode.</div>
    </div>
  );
}

function RecentSection({ visits, activeIdx, onSelect }) {
  if (!visits || visits.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-slate-500" data-testid="global-search-no-recent">
        <Sparkles className="w-8 h-8 mx-auto mb-3 text-slate-300" />
        Begin te typen om te zoeken door alle klanten, facturen, offertes en meer.
      </div>
    );
  }
  return (
    <div>
      <div className="px-4 py-2 text-[10px] font-semibold text-slate-500 bg-slate-50 uppercase tracking-[0.15em] flex items-center gap-2">
        <Clock className="w-3 h-3" /> Recent bezocht
      </div>
      <ul className="divide-y divide-slate-50">
        {visits.map((v, i) => (
          <li key={`${v.key}-${v.entity_id ?? "root"}-${i}`}>
            <ResultRow
              item={{
                title: v.entity_label || v.label,
                meta: v.entity_id ? `${v.label} · ${v.entity_id}` : v.label,
                nav_key: v.key,
                entity_id: v.entity_id,
                icon: "Clock",
              }}
              active={activeIdx === i}
              onClick={() => onSelect({
                nav_key: v.key,
                entity_id: v.entity_id,
                title: v.entity_label || v.label,
              })}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResultsList({ categories, activeIdx, onSelect }) {
  let cursor = 0;
  return (
    <div>
      {categories.map((c) => {
        const CategoryIcon = CATEGORY_ICONS[c.key];
        return (
          <div key={c.key} data-testid={`search-group-${c.key}`}>
            <div className="px-4 py-2 text-[10px] font-semibold text-slate-500 bg-slate-50 uppercase tracking-[0.15em] flex items-center gap-2">
              {CategoryIcon && <CategoryIcon className="w-3 h-3" />}
              {c.label}
              <span className="ml-1 text-slate-400 normal-case tracking-normal">· {c.items.length}</span>
            </div>
            <ul className="divide-y divide-slate-50">
              {c.items.map((it) => {
                const idx = cursor++;
                return (
                  <li key={`${c.key}-${it.id}`}>
                    <ResultRow
                      item={it}
                      active={activeIdx === idx}
                      onClick={() => onSelect(it)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ResultRow({ item, active, onClick }) {
  const CategoryIcon = CATEGORY_ICONS[item.category] || CATEGORY_ICONS[item.nav_key] || Search;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 border-l-2 transition-colors duration-100 ${
        active
          ? "bg-brand-50 border-brand-500 text-brand-900"
          : "border-transparent hover:bg-slate-50 hover:border-slate-200"
      }`}
      data-testid={`search-result-${item.nav_key}-${item.entity_id ?? "root"}`}
    >
      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${active ? "bg-brand-100 text-brand-500" : "bg-slate-100 text-slate-500"}`}>
        <CategoryIcon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{item.title || "—"}</div>
        {item.meta && <div className="text-xs text-slate-500 truncate">{item.meta}</div>}
      </div>
    </button>
  );
}

/** Hook: registreer ⌘K / Ctrl+K globaal om de search-modal te openen. */
export function useGlobalSearchShortcut(onOpen) {
  useEffect(() => {
    const handler = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isCmdK) {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpen]);
}

export { KEY_TIP };
